import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isClientsOpen, setIsClientsOpen] = useState(false);
  const location = useLocation();
  const profileRef = useRef<HTMLDivElement>(null);
  const clientsRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
      if (clientsRef.current && !clientsRef.current.contains(event.target as Node)) {
        setIsClientsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'Request Quote', href: '/quotation' },
    { name: 'Client Portal', href: '/client/login', isButton: true },
  ];

  const profileItems = [
    { name: 'About Us', href: '/profile/about' },
    { name: 'Our Team', href: '/profile/team' },
    { name: 'Company Profile', href: '/profile/company' },
  ];

  const clientItems = [
    { name: 'Our Clients', href: '/clients' },
    // { name: 'Client Testimonials', href: '/clients/testimonials' },
    { name: 'Case Studies', href: '/clients/case-studies' },
  ];

  return (
    <header className="bg-white/95 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 animate-fadeIn">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="flex items-center">
              <div className="text-5xl font-bold text-avis-blue">
                <span className="relative">
                  AVI'S

                  {/* <div className="absolute -top-1 right-0 w-2 h-8 bg-avis-blue transform translate-x-1">
                    <div className="absolute top-0 right-0 w-1 h-1 bg-avis-blue rounded-full"></div>
                    <div className="absolute top-2 right-0 w-2 h-1 bg-avis-blue rounded-full"></div>
                    <div className="absolute top-4 right-0 w-3 h-1 bg-avis-blue rounded-full"></div>
                  </div> */}
                </span>
              </div>
              <div className="ml-2">
                <div className="text-sm text-avis-light-blue font-medium leading-tight">
                  ENGINEERING & CONSULTANCY PVT.LTD
                </div>
                <div className="text-sm text-avis-light-blue font-medium">
                  NDTSERVICES 
                </div>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navigation.map((item) => (
              item.isButton ? (
                <Link
                  key={item.name}
                  to={item.href}
                  className="bg-gradient-to-r from-avis-blue to-avis-dark text-white px-5 py-2.5 rounded-xl text-sm font-semibold 
                           shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 
                           active:translate-y-0"
                >
                  {item.name}
                </Link>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative group ${
                    location.pathname === item.href
                      ? 'text-avis-blue bg-blue-50 font-semibold'
                      : 'text-gray-700 hover:text-avis-blue hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                  {location.pathname === item.href && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-avis-blue rounded-full"></span>
                  )}
                </Link>
              )
            ))}
            
            {/* Profile Dropdown */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => {
                  setIsProfileOpen(!isProfileOpen);
                  setIsClientsOpen(false);
                }}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center ${
                  location.pathname.startsWith('/profile')
                    ? 'text-avis-blue bg-blue-50'
                    : 'text-gray-700 hover:text-avis-blue hover:bg-gray-50'
                }`}
              >
                Profile
                <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isProfileOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 animate-scaleIn">
                  {profileItems.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent hover:text-avis-blue 
                               transition-all duration-200 hover:pl-6 rounded-lg mx-1"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Clients Dropdown */}
            <div className="relative" ref={clientsRef}>
              <button
                onClick={() => {
                  setIsClientsOpen(!isClientsOpen);
                  setIsProfileOpen(false);
                }}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center ${
                  location.pathname.startsWith('/clients')
                    ? 'text-avis-blue bg-blue-50'
                    : 'text-gray-700 hover:text-avis-blue hover:bg-gray-50'
                }`}
              >
                Clients
                <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isClientsOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 animate-scaleIn">
                  {clientItems.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent hover:text-avis-blue 
                               transition-all duration-200 hover:pl-6 rounded-lg mx-1"
                      onClick={() => setIsClientsOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-avis-blue focus:outline-none focus:text-avis-blue"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                    item.isButton
                      ? 'bg-avis-blue text-white hover:bg-avis-dark'
                      : location.pathname === item.href
                      ? 'text-avis-blue bg-blue-50'
                      : 'text-gray-700 hover:text-avis-blue hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Profile Section */}
              <div className="pt-2">
                <div className="text-sm font-semibold text-gray-500 px-3 py-2">Profile</div>
                {profileItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block px-6 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-avis-blue transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              {/* Mobile Clients Section */}
              <div className="pt-2">
                <div className="text-sm font-semibold text-gray-500 px-3 py-2">Clients</div>
                {clientItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block px-6 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-avis-blue transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
